import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";

// reactstrap components
import { Card, CardBody, CardHeader, Button, Row, Col, Badge, Collapse } from "reactstrap";

// services
import { showMessage } from "../../../services/message.js";
import {
  ListarAvisosNaoLidos,
  ListarAvisosLidos,
  MarcarAvisoComoLido,
} from "../../../services/correio_interno_service.js";

//Context
import { useAuth } from "../../../context/auth";

// O HTML vem sanitizado do servidor; sanitizar de novo no cliente evita
// que qualquer conteudo antigo ou adulterado execute script no portal.
const htmlSeguro = (html) => ({
  __html: DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } }),
});

const formataData = (data) =>
  data ? new Date(data).toLocaleDateString("pt-BR") : "";

export default function AvisosCorreioInterno({ protocoloId }) {
  const { setLoading } = useAuth();

  const [naoLidos, set_naoLidos] = useState([]);
  const [lidos, set_lidos] = useState([]);
  const [mostrarLidos, set_mostrarLidos] = useState(false);
  const [avisoAbertoId, set_avisoAbertoId] = useState(null);

  const carregarNaoLidos = () => {
    if (!protocoloId) return;

    ListarAvisosNaoLidos(protocoloId)
      .then((result) => set_naoLidos(result.data || []))
      .catch((err) => showMessage("Aviso", err.message, "error", null));
  };

  const carregarLidos = () => {
    if (!protocoloId) return;

    setLoading(true);
    ListarAvisosLidos(protocoloId)
      .then((result) => set_lidos(result.data || []))
      .catch((err) => showMessage("Aviso", err.message, "error", null))
      .finally(() => setLoading(false));
  };

  const handleMarcarLido = (aviso) => {
    setLoading(true);
    MarcarAvisoComoLido(protocoloId, aviso.correioInterno_Id)
      .then(() => {
        set_naoLidos((atuais) =>
          atuais.filter((a) => a.correioInterno_Id !== aviso.correioInterno_Id)
        );
        if (mostrarLidos) carregarLidos();
      })
      .catch((err) => showMessage("Aviso", err.message, "error", null))
      .finally(() => setLoading(false));
  };

  const handleMostrarLidos = () => {
    const proximoEstado = !mostrarLidos;
    set_mostrarLidos(proximoEstado);
    if (proximoEstado) carregarLidos();
  };

  useEffect(() => {
    carregarNaoLidos();
  }, [protocoloId]);

  // Sem avisos pendentes e sem a aba de lidos aberta, a tela fica limpa.
  if (naoLidos.length === 0 && !mostrarLidos) {
    return (
      <Row>
        <Col md="12" className="text-right">
          <Button color="link" onClick={handleMostrarLidos}>
            Ver avisos lidos
          </Button>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {/* O editor do Gestor grava alinhamento, fonte e tamanho como estilo inline, mas o
          recuo sai como classe ql-indent-N, que sem o CSS do Quill nao renderiza aqui. */}
      <style>
        {`
          .aviso-correio-interno .ql-indent-1 { padding-left: 3em; }
          .aviso-correio-interno .ql-indent-2 { padding-left: 6em; }
          .aviso-correio-interno .ql-indent-3 { padding-left: 9em; }
          .aviso-correio-interno .ql-indent-4 { padding-left: 12em; }
          .aviso-correio-interno blockquote {
            border-left: 4px solid #cccccc;
            padding-left: 16px;
            margin-left: 0;
          }
          .aviso-correio-interno pre {
            background: #f4f3ef;
            padding: 10px;
            border-radius: 4px;
            white-space: pre-wrap;
          }
          .aviso-correio-interno img { max-width: 100%; }
        `}
      </style>
      <Row>
        <Col md="12">
          {naoLidos.length > 0 && (
            <h6>
              Avisos{" "}
              <Badge color="warning" pill>
                {naoLidos.length}
              </Badge>
            </h6>
          )}
        </Col>
      </Row>

      {naoLidos.map((aviso) => (
        <Row key={aviso.correioInterno_Id}>
          <Col md="12">
            <Card style={{ borderLeft: "6px solid #ff9800" }}>
              <CardHeader>
                <strong>{aviso.titulo}</strong>
                <br />
                <small>{formataData(aviso.dataEnvio)}</small>
              </CardHeader>
              <CardBody>
                <div className="aviso-correio-interno" dangerouslySetInnerHTML={htmlSeguro(aviso.descricao)} />
                {aviso.urlAnexo && (
                  <div className="mt-2">
                    <a href={aviso.urlAnexo} target="_blank" rel="noopener noreferrer">
                      <i className="fa fa-paperclip"></i> {aviso.nomeAnexo || "Baixar anexo"}
                    </a>
                  </div>
                )}
                <div className="text-right mt-2">
                  <Button color="success" onClick={() => handleMarcarLido(aviso)}>
                    <i className="fa fa-check"></i> Marcar como lido
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ))}

      <Row>
        <Col md="12" className="text-right">
          <Button color="link" onClick={handleMostrarLidos}>
            {mostrarLidos ? "Ocultar avisos lidos" : "Ver avisos lidos"}
          </Button>
        </Col>
      </Row>

      <Collapse isOpen={mostrarLidos}>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <strong>Avisos lidos</strong>
              </CardHeader>
              <CardBody>
                {lidos.length === 0 && <small>Nenhum aviso lido.</small>}
                {lidos.map((aviso) => (
                  <div key={aviso.correioInterno_Id} className="mb-2">
                    <Button
                      color="link"
                      className="p-0"
                      onClick={() =>
                        set_avisoAbertoId(
                          avisoAbertoId === aviso.correioInterno_Id ? null : aviso.correioInterno_Id
                        )
                      }
                    >
                      {aviso.titulo} — lido em {formataData(aviso.dataLeitura)}
                    </Button>
                    <Collapse isOpen={avisoAbertoId === aviso.correioInterno_Id}>
                      <div className="aviso-correio-interno" dangerouslySetInnerHTML={htmlSeguro(aviso.descricao)} />
                      {aviso.urlAnexo && (
                        <div className="mt-2">
                          <a href={aviso.urlAnexo} target="_blank" rel="noopener noreferrer">
                            <i className="fa fa-paperclip"></i> {aviso.nomeAnexo || "Baixar anexo"}
                          </a>
                        </div>
                      )}
                    </Collapse>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Collapse>
    </>
  );
}
